import { rd, RemoteData } from '@passionware/monads';
import { matchPath } from 'react-router-dom';
import { appRouting } from 'v5/core/routing/appRouting';

const orgDashboardPathPattern = appRouting.org().organization.dashboard();

/** `/app/{org}/dashboard` — not a workspace dashboard. */
export function isOrganizationDashboardPath(pathname: string): boolean {
  return (
    matchPath(pathname, {
      path: orgDashboardPathPattern,
      exact: true
    }) != null
  );
}

/**
 * The dashboard lives inside a workspace, so the bare organization URL sends
 * the user to one — the first by slug. Null when the organization has none.
 */
export function resolveOrgDashboardRedirectPath(
  organizationId: string,
  workspaces: readonly { slug: string }[]
): string | null {
  const first = [...workspaces].sort((a, b) => a.slug.localeCompare(b.slug))[0];
  if (!first) {
    return null;
  }
  return appRouting.org(organizationId).workspace(first.slug).dashboard();
}

/**
 * Keep the org-dashboard chrome as a skeleton until we know we are staying on
 * the legacy org dashboard, or the URL has moved to a workspace dashboard.
 * Only relevant when the shell `enterprise` flag is on (or still loading).
 */
export function shouldHoldMainSidebarOnOrgDashboard(
  pathname: string,
  workspacesRd: RemoteData<readonly unknown[]>,
  enterpriseRd: RemoteData<boolean>
): boolean {
  if (!isOrganizationDashboardPath(pathname)) {
    return false;
  }
  if (rd.isIdle(enterpriseRd) || rd.isPending(enterpriseRd)) {
    return true;
  }
  if (!rd.getOrElse(enterpriseRd, false)) {
    return false;
  }
  if (rd.isIdle(workspacesRd) || rd.isPending(workspacesRd)) {
    return true;
  }
  return rd.mapOrElse(workspacesRd, workspaces => workspaces.length > 0, false);
}
